import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import videoService from '../../../services/videoService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

const QualityList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [qualities, setQualities] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} qualities`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: ''
  });

  const getQualities = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.search,
        ...params
      };
      
      console.log('Fetching qualities with params:', requestParams);
      const response = await videoService.getAllVideoQuality(requestParams);
      console.log('Qualities API Response:', response);
      
      if (response.data && response.data.status) {
        const qualitiesData = response.data.data?.data || [];
        const paginationData = response.data.data?.pagination || {};
        
        console.log('Qualities data:', qualitiesData);
        console.log('Pagination data:', paginationData);
        
        // Ensure qualitiesData is an array
        if (Array.isArray(qualitiesData)) {
          setQualities(qualitiesData);
          setPagination(prev => ({
            ...prev,
            total: paginationData.total || qualitiesData.length,
            current: paginationData.currentPage || prev.current
          }));
        } else {
          console.error('Qualities data is not an array:', qualitiesData);
          setQualities([]);
        }
      } else {
        message.error('Failed to fetch video qualities');
        setQualities([]);
      }
    } catch (error) {
      console.error('Error fetching qualities:', error);
      message.error('Failed to fetch video qualities');
      setQualities([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, pagination.pageSize, pagination.current]);

  // Load qualities data
  useEffect(() => {
    getQualities();
  }, [getQualities]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getQualities({
      searchText: value,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getQualities({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search
    });
  };

  // Handle add quality
  const handleAddQuality = () => {
    navigate('/Video/Quality/addQuality');
  };

  // Handle edit quality
  const handleEditQuality = (vqId) => {
    navigate(`/Video/Quality/editQuality/${vqId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Quality',
      dataIndex: 'quality_name',
      key: 'quality_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.quality_name.localeCompare(b.quality_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'vq_is_active',
      key: 'vq_is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditQuality(record.vq_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Video Qualities</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Video Quality List"
      desc="Manage video qualities and their status"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Video Qualities ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddQuality}
              size="large"
            >
              Add Video Quality
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search qualities..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={qualities}
          rowKey="vq_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{ 
            emptyText: 'No video qualities found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default QualityList;